/**
 * Commandes des écrans de réglages (design system, LigneDeReglage, Interrupteur, ChampNombre,
 * ChampDuree, ChampHeure, Onglets). Chaque réglage s'enregistre dès qu'il change.
 */
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cx, ESPACE_INSECABLE, nombre } from '../outils';
import { Icone } from './Icone';
import { MessageDeChamp } from './MessageDeChamp';

// ---------------------------------------------------------------------------------------
// Groupes et lignes
// ---------------------------------------------------------------------------------------

export function GroupeDeReglages({
  titre,
  description,
  children,
}: {
  titre: string;
  description?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section className="fc-ligne-de-reglage-groupe" aria-labelledby={`${id}-titre`}>
      <header className="fc-ligne-de-reglage-groupe__entete">
        <h2 className="fc-ligne-de-reglage-groupe__titre" id={`${id}-titre`}>
          {titre}
        </h2>
        {description && <p className="fc-ligne-de-reglage-groupe__description">{description}</p>}
      </header>
      <div className="fc-ligne-de-reglage-groupe__panneau">{children}</div>
    </section>
  );
}

export function ReglagesDependants({
  desactives,
  children,
}: {
  desactives: boolean;
  children: ReactNode;
}) {
  return (
    <fieldset className="fc-ligne-de-reglage-dependances" disabled={desactives}>
      {children}
    </fieldset>
  );
}

export interface IdentifiantsLigne {
  idCommande: string;
  libelleId: string;
  descriptionId: string;
}

export interface LigneDeReglageProps {
  libelle: string;
  description?: ReactNode;
  /** La commande, construite avec les identifiants de la ligne. */
  commande: (ids: IdentifiantsLigne) => ReactNode;
  modifiee?: boolean;
  valeurParDefaut?: string;
  onRetablir?: () => void;
  message?: { ton: 'erreur' | 'avertissement'; texte: ReactNode };
}

export function LigneDeReglage({
  libelle,
  description,
  commande,
  modifiee = false,
  valeurParDefaut,
  onRetablir,
  message,
}: LigneDeReglageProps) {
  const id = useId();
  const ids: IdentifiantsLigne = {
    idCommande: `${id}-commande`,
    libelleId: `${id}-libelle`,
    descriptionId: `${id}-aide`,
  };
  return (
    <div className="fc-ligne-de-reglage">
      <div className="fc-ligne-de-reglage__textes">
        <label className="fc-ligne-de-reglage__libelle" htmlFor={ids.idCommande} id={ids.libelleId}>
          {libelle}
        </label>
        {description && (
          <p className="fc-ligne-de-reglage__description" id={ids.descriptionId}>
            {description}
          </p>
        )}
      </div>
      <div className="fc-ligne-de-reglage__commande">
        {modifiee && onRetablir && (
          <button
            type="button"
            className="fc-bouton fc-bouton--discret fc-bouton--petit fc-bouton--icone fc-ligne-de-reglage__retablir"
            aria-label={`Rétablir la valeur par défaut${ESPACE_INSECABLE}: ${valeurParDefaut ?? ''}`}
            title={`Rétablir la valeur par défaut${ESPACE_INSECABLE}: ${valeurParDefaut ?? ''}`}
            onClick={() => {
              onRetablir();
              document.getElementById(ids.idCommande)?.focus();
            }}
          >
            <Icone nom="rotate-ccw" />
          </button>
        )}
        {commande(ids)}
      </div>
      {message && (
        <MessageDeChamp id={`${id}-message`} ton={message.ton}>
          {message.texte}
        </MessageDeChamp>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Interrupteur
// ---------------------------------------------------------------------------------------

export function Interrupteur({
  allume,
  onChange,
  id,
  libelleId,
  etiquette,
  descriptionId,
  desactive = false,
}: {
  allume: boolean;
  onChange: (allume: boolean) => void;
  id?: string;
  libelleId?: string;
  etiquette?: string;
  descriptionId?: string;
  desactive?: boolean;
}) {
  return (
    <button
      type="button"
      className="fc-interrupteur"
      role="switch"
      aria-checked={allume}
      id={id}
      aria-labelledby={libelleId}
      aria-label={libelleId ? undefined : etiquette}
      aria-describedby={descriptionId}
      disabled={desactive}
      onClick={() => onChange(!allume)}
    >
      <span className="fc-interrupteur__pastille" aria-hidden="true" />
    </button>
  );
}

// ---------------------------------------------------------------------------------------
// Nombre et heure
// ---------------------------------------------------------------------------------------

type Unite = string | { singulier: string; pluriel: string };

function texteUnite(unite: Unite | undefined, valeur: number): string {
  if (unite === undefined) return '';
  if (typeof unite === 'string') return unite;
  return Math.abs(valeur) >= 2 ? unite.pluriel : unite.singulier;
}

export interface ChampNombreProps {
  valeur: number;
  onChange: (valeur: number) => void;
  min: number;
  max: number;
  pas?: number;
  unite?: Unite;
  id?: string;
  libelleId?: string;
  descriptionId?: string;
  erreur?: string;
  taille?: 'normale' | 'compacte';
  desactive?: boolean;
  /** Boucle (heures : 23 h puis 0 h). */
  boucle?: boolean;
  /** Heure : `fc-champ-heure`, libellés « Heure précédente / suivante ». */
  heure?: boolean;
}

export function ChampNombre({
  valeur,
  onChange,
  min,
  max,
  pas = 1,
  unite,
  id,
  libelleId,
  descriptionId,
  erreur,
  taille = 'normale',
  desactive = false,
  boucle = false,
  heure = false,
}: ChampNombreProps) {
  const genere = useId();
  const [saisie, setSaisie] = useState(nombre(valeur));
  const [invalide, setInvalide] = useState(false);
  useEffect(() => {
    setSaisie(nombre(valeur));
    setInvalide(false);
  }, [valeur]);
  const idMessage = `${genere}-message`;

  const fixer = (v: number) => {
    let n = v;
    if (boucle) n = ((n - min + (max - min + 1)) % (max - min + 1)) + min;
    n = Math.min(max, Math.max(min, n));
    setSaisie(nombre(n));
    setInvalide(false);
    if (n !== valeur) onChange(n);
  };

  const valider = () => {
    const texte = saisie
      .replace(/[\s  ]/g, '')
      .replace(/h$/i, '')
      .replace(/:00$/, '')
      .replace(',', '.');
    const n = Number(texte);
    if (texte === '' || !Number.isInteger(n) || n < min || n > max) {
      setInvalide(true);
      return;
    }
    fixer(n);
  };

  const clavier = (e: KeyboardEvent<HTMLInputElement>) => {
    const grand = e.shiftKey ? 10 : 1;
    if (e.key === 'ArrowUp') fixer(valeur + pas * grand);
    else if (e.key === 'ArrowDown') fixer(valeur - pas * grand);
    else if (e.key === 'PageUp') fixer(valeur + pas * 10);
    else if (e.key === 'PageDown') fixer(valeur - pas * 10);
    else if (e.key === 'Home') fixer(min);
    else if (e.key === 'End') fixer(max);
    else if (e.key === 'Enter') valider();
    else return;
    e.preventDefault();
  };

  const messageErreur =
    erreur ??
    (invalide
      ? heure
        ? 'Saisissez une heure entre 0 h et 23 h, par exemple 4 h.'
        : `Saisissez une valeur entre ${nombre(min)} et ${nombre(max)}${unite ? ` ${texteUnite(unite, max)}` : ''}.`
      : undefined);

  return (
    <>
      <div
        className={cx(
          'fc-champ fc-champ-nombre',
          heure && 'fc-champ-heure',
          taille === 'compacte' && 'fc-champ--compact',
        )}
      >
        <input
          className="fc-champ__saisie"
          id={id}
          type="text"
          inputMode="numeric"
          role="spinbutton"
          autoComplete="off"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={valeur}
          aria-valuetext={`${nombre(valeur)}${unite ? ` ${texteUnite(unite, valeur)}` : ''}`}
          aria-labelledby={libelleId}
          aria-describedby={
            [messageErreur ? idMessage : null, descriptionId].filter(Boolean).join(' ') || undefined
          }
          aria-invalid={messageErreur ? true : undefined}
          disabled={desactive}
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          onBlur={valider}
          onKeyDown={clavier}
        />
        {unite && (
          <span className="fc-champ__suffixe" aria-hidden="true">
            {texteUnite(unite, valeur)}
          </span>
        )}
        <span className="fc-champ__pas">
          <button
            type="button"
            className="fc-champ__bouton"
            tabIndex={-1}
            aria-label={heure ? 'Heure précédente' : 'Diminuer'}
            disabled={desactive || (!boucle && valeur <= min)}
            onClick={() => fixer(valeur - pas)}
          >
            <Icone nom="minus" />
          </button>
          <button
            type="button"
            className="fc-champ__bouton"
            tabIndex={-1}
            aria-label={heure ? 'Heure suivante' : 'Augmenter'}
            disabled={desactive || (!boucle && valeur >= max)}
            onClick={() => fixer(valeur + pas)}
          >
            <Icone nom="plus" />
          </button>
        </span>
      </div>
      {messageErreur && !erreur && (
        <MessageDeChamp id={idMessage} ton="erreur">
          {messageErreur}
        </MessageDeChamp>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------
// Durée
// ---------------------------------------------------------------------------------------

export type UniteDuree = 'min' | 'h' | 'j';
export interface Duree {
  nombre: number;
  unite: UniteDuree;
}

const MINUTES_PAR_UNITE: Record<UniteDuree, number> = { min: 1, h: 60, j: 1440 };

/** Durée affichée pour un nombre de minutes : la plus grande unité qui tombe juste. */
export function dureeDepuisMinutes(minutes: number): Duree {
  if (minutes > 0 && minutes % 1440 === 0) return { nombre: minutes / 1440, unite: 'j' };
  if (minutes > 0 && minutes % 60 === 0) return { nombre: minutes / 60, unite: 'h' };
  return { nombre: minutes, unite: 'min' };
}

export function minutesDeLaDuree(d: Duree): number {
  return Math.round(d.nombre * MINUTES_PAR_UNITE[d.unite]);
}

export function texteDuree(d: Duree): string {
  return `${nombre(d.nombre, 2)}${ESPACE_INSECABLE}${d.unite}`;
}

/** Lit « 90 », « 1,5 h », « 2 j », « 10 min » ; `null` si illisible. */
export function lireDuree(texte: string, uniteActuelle: UniteDuree): Duree | null {
  const m = /^\s*(\d+(?:[.,]\d+)?)\s*(min|mn|m|h|j|d)?\s*$/i.exec(texte);
  if (!m) return null;
  const n = Number(m[1]!.replace(',', '.'));
  const u = (m[2] ?? '').toLowerCase();
  const unite: UniteDuree =
    u === 'h' ? 'h' : u === 'j' || u === 'd' ? 'j' : u === '' ? uniteActuelle : 'min';
  if (!(n > 0)) return null;
  return { nombre: n, unite };
}

/**
 * Durée à la manière d'Apple : la valeur s'affiche dans une capsule (« 10 min ») ; un clic ouvre
 * par-dessus un popover en verre avec le nombre et l'unité (min, h, j). La durée s'enregistre à
 * la validation (Entrée), au choix d'une unité ou à la fermeture ; Échap annule.
 */
export function ChampDuree({
  minutes,
  onChange,
  id,
  libelleId,
  idsLibelle,
  descriptionId,
  erreur,
  taille = 'normale',
  desactive = false,
  maxMinutes = 525600,
  autoFocus,
}: {
  /** Valeur en minutes, ou `null` si le champ est vide. */
  minutes: number | null;
  onChange: (minutes: number) => void;
  id?: string;
  libelleId?: string;
  /** Tableau des étapes : en-têtes qui nomment la saisie. */
  idsLibelle?: string[];
  descriptionId?: string;
  erreur?: boolean;
  taille?: 'normale' | 'compacte';
  desactive?: boolean;
  maxMinutes?: number;
  autoFocus?: boolean;
}) {
  const genere = useId();
  const [ouvert, setOuvert] = useState(false);
  const [unite, setUnite] = useState<UniteDuree>(
    minutes === null ? 'min' : dureeDepuisMinutes(minutes).unite,
  );
  const [saisie, setSaisie] = useState(
    minutes === null ? '' : nombre(dureeDepuisMinutes(minutes).nombre, 2),
  );
  const [invalide, setInvalide] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number; haut: boolean } | null>(
    null,
  );
  const bouton = useRef<HTMLButtonElement>(null);
  const panneau = useRef<HTMLDivElement>(null);
  const champ = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (minutes === null || ouvert) return;
    const d = dureeDepuisMinutes(minutes);
    setUnite(d.unite);
    setSaisie(nombre(d.nombre, 2));
    setInvalide(false);
  }, [minutes, ouvert]);

  /** Enregistre la durée ; `false` si elle n'est pas valable. */
  const transmettre = (d: Duree | null): boolean => {
    const m = d ? minutesDeLaDuree(d) : 0;
    if (!d || m < 1 || m > maxMinutes) {
      setInvalide(true);
      return false;
    }
    setInvalide(false);
    setUnite(d.unite);
    setSaisie(nombre(d.nombre, 2));
    if (m !== minutes) onChange(m);
    return true;
  };

  const fermer = (garder: boolean) => {
    if (garder && saisie.trim() !== '' && !transmettre(lireDuree(saisie, unite))) return;
    if (!garder && minutes !== null) {
      const d = dureeDepuisMinutes(minutes);
      setUnite(d.unite);
      setSaisie(nombre(d.nombre, 2));
      setInvalide(false);
    }
    setOuvert(false);
    bouton.current?.focus();
  };

  // Placement sous la capsule (au-dessus s'il manque de place), dans la fenêtre du navigateur.
  useLayoutEffect(() => {
    if (!ouvert) return;
    const placer = () => {
      const r = bouton.current?.getBoundingClientRect();
      const largeur = panneau.current?.offsetWidth ?? 280;
      const hauteur = panneau.current?.offsetHeight ?? 160;
      if (!r) return;
      const haut = r.bottom + 8 + hauteur > window.innerHeight - 8 && r.top - 8 - hauteur > 8;
      setPosition({
        left: Math.min(
          Math.max(8, r.left + r.width / 2 - largeur / 2),
          window.innerWidth - largeur - 8,
        ),
        top: haut ? r.top - 8 - hauteur : r.bottom + 8,
        haut,
      });
    };
    placer();
    champ.current?.select();
    window.addEventListener('resize', placer);
    window.addEventListener('scroll', placer, true);
    return () => {
      window.removeEventListener('resize', placer);
      window.removeEventListener('scroll', placer, true);
    };
  }, [ouvert]);

  // Un clic ailleurs ferme le popover en gardant la saisie, comme sur iOS.
  useEffect(() => {
    if (!ouvert) return;
    const dehors = (e: PointerEvent) => {
      const cible = e.target as Node;
      if (panneau.current?.contains(cible) || bouton.current?.contains(cible)) return;
      fermer(true);
    };
    document.addEventListener('pointerdown', dehors);
    return () => document.removeEventListener('pointerdown', dehors);
  });

  const idMessage = `${genere}-message`;
  const affiche = minutes === null ? null : texteDuree(dureeDepuisMinutes(minutes));
  return (
    <>
      <button
        ref={bouton}
        type="button"
        id={id}
        className={cx('fc-champ-duree', taille === 'compacte' && 'fc-champ-duree--compact')}
        autoFocus={autoFocus}
        disabled={desactive}
        aria-haspopup="dialog"
        aria-expanded={ouvert}
        aria-labelledby={
          idsLibelle
            ? [...idsLibelle, genere].join(' ')
            : libelleId
              ? `${libelleId} ${genere}`
              : undefined
        }
        aria-invalid={erreur || invalide ? true : undefined}
        aria-describedby={
          [invalide && !ouvert ? idMessage : null, descriptionId].filter(Boolean).join(' ') ||
          undefined
        }
        onClick={() => (ouvert ? fermer(true) : setOuvert(true))}
      >
        <span
          id={genere}
          className="fc-champ-duree__valeur"
          data-vide={affiche === null || undefined}
        >
          {affiche ?? 'Durée'}
        </span>
        <Icone nom="chevron-down" taille="petite" className="fc-champ-duree__chevron" />
      </button>
      {ouvert &&
        createPortal(
          <div
            ref={panneau}
            className="fc-popover fc-champ-duree__popover"
            role="dialog"
            aria-label="Durée"
            data-haut={position?.haut || undefined}
            style={{
              position: 'fixed',
              left: position?.left ?? -9999,
              top: position?.top ?? -9999,
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                fermer(false);
              }
            }}
          >
            <div className="fc-champ fc-champ-duree__nombre">
              <input
                ref={champ}
                className="fc-champ__saisie"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                spellCheck={false}
                aria-label="Nombre"
                aria-invalid={invalide || undefined}
                aria-describedby={invalide ? idMessage : undefined}
                value={saisie}
                onChange={(e) => {
                  setSaisie(e.target.value);
                  setInvalide(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    fermer(true);
                  }
                }}
              />
            </div>
            <div
              className="fc-commande-segmentee fc-commande-segmentee--large"
              role="radiogroup"
              aria-label="Unité"
            >
              {(
                [
                  ['min', 'minutes'],
                  ['h', 'heures'],
                  ['j', 'jours'],
                ] as const
              ).map(([valeur, nom]) => (
                <button
                  key={valeur}
                  type="button"
                  role="radio"
                  aria-checked={unite === valeur}
                  aria-label={nom}
                  className="fc-commande-segmentee__segment"
                  onClick={() => {
                    setUnite(valeur);
                    const d = lireDuree(saisie, valeur);
                    if (d) transmettre({ nombre: d.nombre, unite: valeur });
                  }}
                >
                  {valeur}
                </button>
              ))}
            </div>
            {invalide && (
              <MessageDeChamp id={idMessage} ton="erreur">
                Saisissez une durée, par exemple 10&nbsp;min.
              </MessageDeChamp>
            )}
          </div>,
          document.body,
        )}
      {invalide && !ouvert && taille !== 'compacte' && (
        <MessageDeChamp id={idMessage} ton="erreur">
          Saisissez une durée, par exemple 10&nbsp;min.
        </MessageDeChamp>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------
// Onglets
// ---------------------------------------------------------------------------------------

export function Onglets<V extends string>({
  onglets,
  valeur,
  onChoisir,
  etiquette,
  panneau,
  classeListe,
  classePanneau = 'fc-onglets__panneau',
}: {
  onglets: { valeur: V; libelle: string }[];
  valeur: V;
  onChoisir: (valeur: V) => void;
  etiquette: string;
  panneau: ReactNode;
  classeListe?: string;
  classePanneau?: string;
}) {
  const id = useId();
  const liste = useRef<HTMLDivElement>(null);
  // L'indicateur glisse sous l'onglet choisi : position et largeur mesurées.
  useLayoutEffect(() => {
    const element = liste.current;
    const choisi = element?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!element || !choisi) return;
    element.style.setProperty('--fc-onglets-x', `${choisi.offsetLeft}px`);
    element.style.setProperty('--fc-onglets-largeur', String(choisi.offsetWidth));
    element.dataset.mesure = '';
  }, [valeur]);
  const clavier = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = onglets.findIndex((o) => o.valeur === valeur);
    let j = i;
    if (e.key === 'ArrowRight') j = (i + 1) % onglets.length;
    else if (e.key === 'ArrowLeft') j = (i - 1 + onglets.length) % onglets.length;
    else if (e.key === 'Home') j = 0;
    else if (e.key === 'End') j = onglets.length - 1;
    else return;
    e.preventDefault();
    onChoisir(onglets[j]!.valeur);
    liste.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[j]?.focus();
  };
  return (
    <>
      <div
        ref={liste}
        className={cx('fc-onglets', classeListe)}
        role="tablist"
        aria-label={etiquette}
        onKeyDown={clavier}
      >
        {onglets.map((o) => (
          <button
            key={o.valeur}
            type="button"
            className="fc-onglets__onglet"
            role="tab"
            id={`${id}-${o.valeur}`}
            aria-selected={o.valeur === valeur}
            aria-controls={`${id}-panneau`}
            tabIndex={o.valeur === valeur ? 0 : -1}
            onClick={() => onChoisir(o.valeur)}
          >
            {o.libelle}
          </button>
        ))}
        <span className="fc-onglets__indicateur" aria-hidden="true" />
      </div>
      <div
        className={classePanneau}
        role="tabpanel"
        id={`${id}-panneau`}
        aria-labelledby={`${id}-${valeur}`}
        tabIndex={0}
      >
        {panneau}
      </div>
    </>
  );
}
